import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiService } from "../services/api";
import AdminTabs from "../components/AdminTabs";
import { toast } from "sonner";

const resolveImageUrl = (url) => {
    if (!url) {
        return "";
    }
    if (url.startsWith("http")) {
        return url;
    }
    if (process.env.REACT_APP_BACKEND_URL) {
        return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
};

const resolveSetlistUrl = (token) => {
    if (!token) {
        return "";
    }
    const frontendBase = (process.env.REACT_APP_FRONTEND_URL || window.location.origin || "").replace(/\/$/, "");
    return `${frontendBase}/setlist/${token}`;
};

const decodeHtml = (value) => {
    if (!value) {
        return "";
    }
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
};

const sanitizeHtml = (value) => {
    if (!value) {
        return "";
    }
    const decoded = decodeHtml(value);
    const parser = new DOMParser();
    const doc = parser.parseFromString(decoded, "text/html");
    const blockedTags = ["script", "style", "iframe", "object", "embed", "link", "meta"];

    blockedTags.forEach((tag) => {
        doc.querySelectorAll(tag).forEach((element) => element.remove());
    });

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
    while (walker.nextNode()) {
        const element = walker.currentNode;
        [...element.attributes].forEach((attr) => {
            const name = attr.name.toLowerCase();
            const value = attr.value || "";
            if (name.startsWith("on") || name === "style") {
                element.removeAttribute(attr.name);
                return;
            }
            if ((name === "href" || name === "src") && value.trim().toLowerCase().startsWith("javascript:")) {
                element.removeAttribute(attr.name);
            }
        });
    }

    return doc.body.innerHTML;
};

const getApiErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") {
        return detail;
    }
    if (Array.isArray(detail)) {
        return detail
            .map((item) => item?.msg || item?.detail || item?.type || "")
            .filter(Boolean)
            .join("; ") || fallback;
    }
    return fallback;
};

const formatDate = (value) => {
    if (!value) {
        return "";
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit"
    }).format(parsed);
};

const formatEuro = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
        return "-";
    }
    return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR"
    }).format(amount);
};

const formatDateTime = (value) => {
    if (!value) {
        return "";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    }).format(parsed);
};

const toDatetimeLocal = (value) => {
    if (!value) {
        return "";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "";
    }
    const pad = (part) => String(part).padStart(2, "0");
    return [
        parsed.getFullYear(),
        "-",
        pad(parsed.getMonth() + 1),
        "-",
        pad(parsed.getDate()),
        "T",
        pad(parsed.getHours()),
        ":",
        pad(parsed.getMinutes())
    ].join("");
};

const MONTH_NAMES = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember"
];

const FLYER_SHORT_DESC_MAX = 140;
const QUARTER_MONTHS = {
    1: [1, 2, 3],
    2: [4, 5, 6],
    3: [7, 8, 9],
    4: [10, 11, 12]
};

const DEADLINE_FIELDS = [
    { value: "title", label: "Titel" },
    { value: "short_description", label: "Kurzbeschreibung" },
    { value: "description", label: "Beschreibung" },
    { value: "admission", label: "Preis" },
    { value: "image_url", label: "Titelbild" },
    { value: "cover_image_url", label: "Coverbild" },
    { value: "ticket_url", label: "Ticket-Link" },
    { value: "homepage_url", label: "Homepage-Link" },
    { value: "pricing_tiers", label: "Preisstaffeln" },
    { value: "gallery_image_urls", label: "Galeriebilder" }
];

const STATUS_OPTIONS = [
    { value: "pending", label: "Ausstehend" },
    { value: "approved", label: "Genehmigt" },
    { value: "wordpress_error", label: "WordPress-Fehler" },
    { value: "rejected", label: "Abgelehnt" },
    { value: "all", label: "Alle" }
];

const hasWordpressIssue = (event) => {
    const status = (event.status || "").trim().toLowerCase();
    const category = decodeHtml(event.category || "").trim().toLowerCase();
    const hasConnection = Boolean(
        event.wordpress_url
        || event.wordpress_id
        || event.wordpress_block_id
        || (event.wordpress_event_refs || []).length
    );
    return Boolean(event.wordpress_publish_error) || (
        event.imported_from_wp !== true
        && category !== "ausstellung"
        && (status === "approved" || status === "published")
        && !hasConnection
    );
};

const GEMA_TYPE_LABELS = {
    TYPE_CONCERT: "Live-Konzert",
    TYPE_LIVE_ENT: "Live-Unterhaltung",
    TYPE_STREET: "Straßenfest / Outdoor",
    TYPE_PARTY: "Party / DJ-Event",
    TYPE_SPORT: "Sportveranstaltung",
    TYPE_BG: "Hintergrundmusik"
};

const SORT_OPTIONS = [
    { value: "date-desc", label: "Termin (neu -> alt)" },
    { value: "date-asc", label: "Termin (alt -> neu)" },
    { value: "created-desc", label: "Eingang (neu -> alt)" },
    { value: "created-asc", label: "Eingang (alt -> neu)" },
    { value: "title-asc", label: "Titel (A-Z)" },
    { value: "title-desc", label: "Titel (Z-A)" }
];

const GEMA_API_FILTERS = {
    ACTIVE: "active",
    MISSING: "missing",
    DONE: "done"
};

const getDefaultNoticeMonthYear = () => {
    const now = new Date();
    const nextMonthIndex = (now.getMonth() + 1) % 12;
    const year = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
    return { month: nextMonthIndex + 1, year };
};

const parseEventDate = (value) => {
    if (!value) {
        return null;
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    return parsed;
};

const normalizeNoticeMonths = (months) => {
    if (!Array.isArray(months)) {
        return [];
    }
    return Array.from(new Set(months))
        .map((month) => Number(month))
        .filter((month) => Number.isInteger(month) && month >= 1 && month <= 12)
        .sort((a, b) => a - b);
};

const resolveQuarterMonths = (quarter) => QUARTER_MONTHS[Number(quarter)] || [];

const toDateInputValue = (year, month, day = 1) => (
    `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
);

const parseDateInputValue = (value) => {
    if (!value) {
        return null;
    }
    const [yearPart, monthPart, dayPart] = value.split("-");
    const year = Number(yearPart);
    const month = Number(monthPart);
    const day = Number(dayPart);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return null;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return null;
    }
    return { year, month, day };
};

const formatNoticeMonthsForFilename = (months) => {
    const normalized = normalizeNoticeMonths(months);
    return normalized.map((month) => String(month).padStart(2, "0")).join("-");
};

const formatNoticeMonthsLabel = (months) => {
    const normalized = normalizeNoticeMonths(months);
    if (normalized.length === 0) {
        return "";
    }
    const labels = normalized
        .map((month) => MONTH_NAMES[month - 1])
        .filter(Boolean);
    if (labels.length === 1) {
        return labels[0];
    }
    const isConsecutive = normalized.every((value, index) => (
        index === 0 || value === normalized[index - 1] + 1
    ));
    if (isConsecutive) {
        return `${labels[0]}-${labels[labels.length - 1]}`;
    }
    return labels.join(", ");
};

const formatNoticeRangeLabel = (startValue, endValue) => {
    const start = parseDateInputValue(startValue);
    const end = parseDateInputValue(endValue);
    if (!start || !end) {
        return "";
    }
    const startIndex = Date.UTC(start.year, start.month - 1, start.day);
    const endIndex = Date.UTC(end.year, end.month - 1, end.day);
    const first = startIndex <= endIndex ? start : end;
    const last = startIndex <= endIndex ? end : start;
    const startLabel = `${String(first.day).padStart(2, "0")}.${String(first.month).padStart(2, "0")}.${first.year}`;
    const endLabel = `${String(last.day).padStart(2, "0")}.${String(last.month).padStart(2, "0")}.${last.year}`;
    if (first.year === last.year && first.month === last.month && first.day === last.day) {
        return startLabel;
    }
    return `${startLabel} - ${endLabel}`;
};

const formatNoticeRangeForFilename = (startValue, endValue) => {
    if (!startValue || !endValue) {
        return "range";
    }
    return `${startValue}_bis_${endValue}`;
};

const formatFlyerDate = (value) => {
    if (!value) {
        return "";
    }
    const parsed = parseEventDate(value);
    if (!parsed) {
        return value;
    }
    const pad = (part) => String(part).padStart(2, "0");
    return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}.`;
};

const formatFlyerTime = (value) => {
    if (!value) {
        return "";
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return "";
    }
    if (/uhr/i.test(trimmed)) {
        return trimmed;
    }
    const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?/);
    if (!match) {
        return trimmed;
    }
    const hour = match[1];
    const minute = match[2];
    if (!minute || minute === "00") {
        return `${hour} Uhr`;
    }
    return `${hour}:${minute} Uhr`;
};

const parseFlyerTimeMinutes = (value) => {
    if (!value) {
        return null;
    }
    const match = value.trim().match(/^(\d{1,2})(?::(\d{2}))?/);
    if (!match) {
        return null;
    }
    const hour = Number(match[1]);
    const minute = Number(match[2] || "0");
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return null;
    }
    return hour * 60 + minute;
};

const getFlyerScheduleEntries = (event) => {
    const rawEntries = Array.isArray(event?.event_dates) ? event.event_dates : [];
    const entries = rawEntries
        .filter((entry) => entry && entry.date && entry.time)
        .map((entry) => ({
            date: entry.date,
            time: entry.time,
            door_time: entry.door_time,
            end_time: entry.end_time
        }));
    if (entries.length > 0) {
        return entries;
    }
    if (!event?.date || !event?.time) {
        return [];
    }
    return [{
        date: event.date,
        time: event.time,
        door_time: event.door_time,
        end_time: event.end_time
    }];
};

const getFlyerDateIndex = (value) => {
    const parsed = parseEventDate(value);
    if (!parsed) {
        return null;
    }
    return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const escapeFlyerHtml = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
};

const normalizeFlyerText = (value) => {
    if (!value) {
        return "";
    }
    const decoded = decodeHtml(value);
    return decoded.replace(/\r\n/g, "\n").trim();
};

const formatFlyerShortDescription = (value) => {
    const normalized = normalizeFlyerText(value);
    if (normalized.length <= FLYER_SHORT_DESC_MAX) {
        return normalized;
    }
    return `${normalized.slice(0, FLYER_SHORT_DESC_MAX - 3).trim()}...`;
};

const AdminPanel = ({ user }) => {
    const [searchParams] = useSearchParams();
    const [pendingEvents, setPendingEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [updatingUserId, setUpdatingUserId] = useState(null);
    const [rejectionNotes, setRejectionNotes] = useState({});
    const [notificationSettings, setNotificationSettings] = useState({
        enabled: false,
        frequency: "off",
        email: ""
    });
    const [screenSettings, setScreenSettings] = useState({
        mode: "all",
        start_date: "",
        end_date: ""
    });
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [screenSettingsLoading, setScreenSettingsLoading] = useState(true);
    const [screenSettingsSaving, setScreenSettingsSaving] = useState(false);
    const [testSending, setTestSending] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [smtpOauthStatus, setSmtpOauthStatus] = useState(null);
    const [smtpOauthLoading, setSmtpOauthLoading] = useState(false);
    const [smtpOauthDevice, setSmtpOauthDevice] = useState(null);
    const [smtpOauthPolling, setSmtpOauthPolling] = useState(false);
    const [smtpOauthError, setSmtpOauthError] = useState("");
    const [deletingEventId, setDeletingEventId] = useState(null);
    const [publishingWordpressId, setPublishingWordpressId] = useState(null);
    const defaultNoticeDate = getDefaultNoticeMonthYear();
    const defaultDeadlineDate = getDefaultNoticeMonthYear();
    const [noticeMode, setNoticeMode] = useState("month");
    const [noticeMonthSelection, setNoticeMonthSelection] = useState(defaultNoticeDate.month);
    const [noticeQuarter, setNoticeQuarter] = useState(Math.ceil(defaultNoticeDate.month / 3));
    const [noticeMonths, setNoticeMonths] = useState([defaultNoticeDate.month]);
    const [noticeRangeStart, setNoticeRangeStart] = useState(
        toDateInputValue(defaultNoticeDate.year, defaultNoticeDate.month, 1)
    );
    const [noticeRangeEnd, setNoticeRangeEnd] = useState(
        toDateInputValue(defaultNoticeDate.year, defaultNoticeDate.month, 1)
    );
    const [noticeYear, setNoticeYear] = useState(defaultNoticeDate.year);
    const [noticeRecipient, setNoticeRecipient] = useState(user?.email || "");
    const noticeRecipientInitializedRef = useRef(Boolean(user?.email));
    const [noticeSubject, setNoticeSubject] = useState("");
    const [noticeAutoSubject, setNoticeAutoSubject] = useState("");
    const [noticeBody, setNoticeBody] = useState("");
    const [noticeHtml, setNoticeHtml] = useState("");
    const [noticeSubjectTouched, setNoticeSubjectTouched] = useState(false);
    const [noticeEventsCount, setNoticeEventsCount] = useState(null);
    const [noticeEvents, setNoticeEvents] = useState([]);
    const [noticeExcludedIds, setNoticeExcludedIds] = useState([]);
    const [noticeLoading, setNoticeLoading] = useState(false);
    const [noticeSending, setNoticeSending] = useState(false);
    const [noticeGenerated, setNoticeGenerated] = useState(false);
    const [noticePreviewLoading, setNoticePreviewLoading] = useState(false);
    const [flyerExporting, setFlyerExporting] = useState(false);
    const [deadlines, setDeadlines] = useState([]);
    const [deadlinesLoading, setDeadlinesLoading] = useState(true);
    const [deadlinesSaving, setDeadlinesSaving] = useState(false);
    const [deadlinesDeletingId, setDeadlinesDeletingId] = useState(null);
    const [editingDeadlineId, setEditingDeadlineId] = useState(null);
    const [assignmentSelections, setAssignmentSelections] = useState({});
    const [assignmentSavingId, setAssignmentSavingId] = useState(null);
    const [gemaExportingId, setGemaExportingId] = useState(null);
    const [gemaDispatching, setGemaDispatching] = useState(false);
    const [gemaApiLoading, setGemaApiLoading] = useState(false);
    const [gemaApiReloginLoading, setGemaApiReloginLoading] = useState(false);
    const [gemaApiData, setGemaApiData] = useState({
        lizenzListe: [],
        veranstaltungsListe: [],
        errors: {}
    });
    const [gemaApiShowCancelled, setGemaApiShowCancelled] = useState(false);
    const [gemaApiFilter, setGemaApiFilter] = useState(GEMA_API_FILTERS.ACTIVE);
    const [deadlineForm, setDeadlineForm] = useState({
        month: defaultDeadlineDate.month,
        year: defaultDeadlineDate.year,
        dueAt: "",
        requiredFields: []
    });
    const [openSections, setOpenSections] = useState({
        notifications: false,
        screen: false,
        notice: false,
        deadlines: false,
        gemaApi: false,
        gema: false,
        events: false,
        users: false
    });
    const [eventFilters, setEventFilters] = useState({
        search: "",
        status: "pending",
        category: "all",
        month: "all",
        year: "all"
    });
    const [eventSort, setEventSort] = useState("date-desc");
    const [eventPage, setEventPage] = useState(1);
    const [eventPageSize, setEventPageSize] = useState(10);
    const reviewEventId = searchParams.get("eventId");

    useEffect(() => {
        fetchPendingEvents();
        fetchUsers();
        fetchNotificationSettings();
        fetchScreenSettings();
        fetchSmtpOauthStatus();
        fetchDeadlines();
    }, []);

    useEffect(() => {
        if (!user?.email || noticeRecipientInitializedRef.current) {
            return;
        }
        setNoticeRecipient(user.email);
        noticeRecipientInitializedRef.current = true;
    }, [user?.email]);

    useEffect(() => {
        if (!reviewEventId) {
            return;
        }
        setEventFilters((prev) => ({
            ...prev,
            search: reviewEventId,
            status: "all",
        }));
        setOpenSections((prev) => ({
            ...prev,
            events: true,
        }));
    }, [reviewEventId]);

    useEffect(() => {
        setEventPage(1);
    }, [eventFilters, eventSort, eventPageSize]);

    const screenUrl = useMemo(() => {
        const base = window.location.origin.replace(/\/$/, "");
        const params = new URLSearchParams();
        if (screenSettings.mode && screenSettings.mode !== "all") {
            params.set("mode", screenSettings.mode);
        }
        if (screenSettings.mode === "range") {
            if (screenSettings.start_date) {
                params.set("start_date", screenSettings.start_date);
            }
            if (screenSettings.end_date) {
                params.set("end_date", screenSettings.end_date);
            }
        }
        const query = params.toString();
        return `${base}/screen${query ? `?${query}` : ""}`;
    }, [screenSettings]);

    const fetchPendingEvents = async () => {
        try {
            setEventsLoading(true);
            const data = await apiService.getEvents();
            setPendingEvents(data);
        } catch (error) {
            console.error("Error fetching pending events:", error);
            toast.error("Fehler beim Laden der Veranstaltungen");
        } finally {
            setEventsLoading(false);
        }
    };

    const handleRefreshEvents = (event) => {
        event.preventDefault();
        fetchPendingEvents();
    };

    const fetchUsers = async () => {
        try {
            setUsersLoading(true);
            const data = await apiService.getUsers();
            setUsers(data);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Fehler beim Laden der Benutzerliste");
        } finally {
            setUsersLoading(false);
        }
    };

    const handleRefreshUsers = (event) => {
        event.preventDefault();
        fetchUsers();
    };

    const fetchNotificationSettings = async () => {
        try {
            setSettingsLoading(true);
            const data = await apiService.getNotificationSettings();
            setNotificationSettings({
                enabled: Boolean(data.enabled),
                frequency: data.frequency || "off",
                email: data.email || ""
            });
        } catch (error) {
            console.error("Error fetching notification settings:", error);
            toast.error("Benachrichtigungseinstellungen konnten nicht geladen werden");
        } finally {
            setSettingsLoading(false);
        }
    };

    const fetchScreenSettings = async () => {
        try {
            setScreenSettingsLoading(true);
            const data = await apiService.getScreenSettings();
            setScreenSettings({
                mode: data.mode || "all",
                start_date: data.start_date || "",
                end_date: data.end_date || ""
            });
        } catch (error) {
            console.error("Error fetching screen settings:", error);
            toast.error("Screen-Einstellungen konnten nicht geladen werden");
        } finally {
            setScreenSettingsLoading(false);
        }
    };

    const fetchSmtpOauthStatus = async () => {
        try {
            setSmtpOauthLoading(true);
            const data = await apiService.getSmtpOauthStatus();
            setSmtpOauthStatus(data);
        } catch (error) {
            console.error("Error fetching SMTP OAuth status:", error);
            toast.error("SMTP OAuth Status konnte nicht geladen werden");
        } finally {
            setSmtpOauthLoading(false);
        }
    };

    const fetchDeadlines = async () => {
        try {
            setDeadlinesLoading(true);
            const data = await apiService.getDeadlines();
            setDeadlines(data || []);
        } catch (error) {
            console.error("Error fetching deadlines:", error);
            toast.error("Deadlines konnten nicht geladen werden");
        } finally {
            setDeadlinesLoading(false);
        }
    };

    const fetchGemaExternalData = async () => {
        try {
            setGemaApiLoading(true);
            const data = await apiService.getGemaExternalData(true);
            setGemaApiData({
                lizenzListe: Array.isArray(data?.lizenzListe) ? data.lizenzListe : [],
                veranstaltungsListe: Array.isArray(data?.veranstaltungsListe) ? data.veranstaltungsListe : [],
                errors: data?.errors || {}
            });
        } catch (error) {
            console.error("Error fetching external GEMA data:", error);
            toast.error(error.response?.data?.detail || "Externe GEMA-Daten konnten nicht geladen werden");
        } finally {
            setGemaApiLoading(false);
        }
    };

    const handleGemaApiRelogin = async () => {
        try {
            setGemaApiReloginLoading(true);
            await apiService.reloginGema();
            toast.success("GEMA-Session neu aufgebaut");
            await fetchGemaExternalData();
        } catch (error) {
            console.error("Error re-authenticating GEMA session:", error);
            toast.error(error.response?.data?.detail || "GEMA Re-Login fehlgeschlagen");
        } finally {
            setGemaApiReloginLoading(false);
        }
    };

    const handleDeadlineFieldToggle = (field) => {
        setDeadlineForm((prev) => {
            const hasField = prev.requiredFields.includes(field);
            return {
                ...prev,
                requiredFields: hasField
                    ? prev.requiredFields.filter((item) => item !== field)
                    : [...prev.requiredFields, field]
            };
        });
    };

    const handleDeadlineChange = (key, value) => {
        setDeadlineForm((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const resetDeadlineForm = () => {
        setEditingDeadlineId(null);
        setDeadlineForm({
            month: defaultDeadlineDate.month,
            year: defaultDeadlineDate.year,
            dueAt: "",
            requiredFields: []
        });
    };

    const handleDeadlineSubmit = async (event) => {
        event.preventDefault();
        if (!deadlineForm.dueAt) {
            toast.error("Bitte eine Deadline mit Datum und Uhrzeit auswählen.");
            return;
        }
        if (deadlineForm.requiredFields.length === 0) {
            toast.error("Bitte mindestens ein Pflichtfeld auswählen.");
            return;
        }
        const parsedDueAt = new Date(deadlineForm.dueAt);
        if (Number.isNaN(parsedDueAt.getTime())) {
            toast.error("Ungültiges Datum für die Deadline.");
            return;
        }
        const payload = {
            month: Number(deadlineForm.month),
            year: Number(deadlineForm.year),
            due_at: parsedDueAt.toISOString(),
            required_fields: deadlineForm.requiredFields
        };
        try {
            setDeadlinesSaving(true);
            if (editingDeadlineId) {
                await apiService.updateDeadline(editingDeadlineId, payload);
                toast.success("Deadline aktualisiert");
            } else {
                await apiService.createDeadline(payload);
                toast.success("Deadline gespeichert");
            }
            resetDeadlineForm();
            fetchDeadlines();
        } catch (error) {
            console.error("Error saving deadline:", error);
            toast.error(error.response?.data?.detail || "Deadline konnte nicht gespeichert werden");
        } finally {
            setDeadlinesSaving(false);
        }
    };

    const handleEditDeadline = (deadline) => {
        setEditingDeadlineId(deadline.id);
        setDeadlineForm({
            month: deadline.month,
            year: deadline.year,
            dueAt: toDatetimeLocal(deadline.due_at),
            requiredFields: deadline.required_fields || []
        });
        setOpenSections((prev) => ({
            ...prev,
            deadlines: true
        }));
    };

    const handleDeleteDeadline = async (deadlineId) => {
        const confirmed = window.confirm("Deadline wirklich löschen?");
        if (!confirmed) {
            return;
        }
        try {
            setDeadlinesDeletingId(deadlineId);
            await apiService.deleteDeadline(deadlineId);
            toast.success("Deadline gelöscht");
            fetchDeadlines();
        } catch (error) {
            console.error("Error deleting deadline:", error);
            toast.error("Deadline konnte nicht gelöscht werden");
        } finally {
            setDeadlinesDeletingId(null);
        }
    };

    const handleApprove = async (eventId) => {
        try {
            const response = await apiService.approveEvent(eventId, "approved");
            if (response?.wordpress_publish_error) {
                toast.error(`Genehmigt, aber WordPress-Push fehlgeschlagen: ${response.wordpress_publish_error}`, {
                    duration: 10000
                });
            } else {
                toast.success("Veranstaltung genehmigt und veröffentlicht");
            }
            fetchPendingEvents();
        } catch (error) {
            console.error("Error approving event:", error);
            toast.error("Fehler beim Genehmigen der Veranstaltung");
        } finally {
            setRejectionNotes(prev => {
                const next = { ...prev };
                delete next[eventId];
                return next;
            });
        }
    };

    const handleWordpressRepublish = async (eventId) => {
        try {
            setPublishingWordpressId(eventId);
            await apiService.publishEvent(eventId);
            toast.success("Veranstaltung auf WordPress veröffentlicht");
            await fetchPendingEvents();
        } catch (error) {
            console.error("Error publishing event to WordPress:", error);
            toast.error(getApiErrorMessage(error, "Veröffentlichung auf WordPress fehlgeschlagen"));
            await fetchPendingEvents();
        } finally {
            setPublishingWordpressId(null);
        }
    };

    const handleReject = async (eventId) => {
        try {
            const rejectionMessage = rejectionNotes[eventId] || "";
            if (!rejectionMessage.trim()) {
                toast.error("Bitte eine Ablehnungsnachricht eingeben.");
                return;
            }
            await apiService.approveEvent(eventId, "rejected", rejectionMessage);
            toast.success("Veranstaltung abgelehnt");
            fetchPendingEvents();
        } catch (error) {
            console.error("Error rejecting event:", error);
            toast.error("Fehler beim Ablehnen der Veranstaltung");
        } finally {
            setRejectionNotes(prev => {
                const next = { ...prev };
                delete next[eventId];
                return next;
            });
        }
    };

    const handleDeleteEvent = async (eventId, title) => {
        const confirmed = window.confirm(`Veranstaltung "${title || "Ohne Titel"}" wirklich loeschen?`);
        if (!confirmed) {
            return;
        }
        try {
            setDeletingEventId(eventId);
            const result = await apiService.deleteEvent(eventId);
            toast.success(result?.soft_deleted ? result.message : "Veranstaltung geloescht");
            fetchPendingEvents();
        } catch (error) {
            const status = error.response?.status;
            if (status === 409) {
                const force = window.confirm(
                    "Loeschen in WordPress fehlgeschlagen. Veranstaltung trotzdem loeschen?"
                );
                if (force) {
                    try {
                        const result = await apiService.deleteEvent(eventId, true);
                        toast.success(result?.soft_deleted ? result.message : "Veranstaltung geloescht");
                        fetchPendingEvents();
                    } catch (retryError) {
                        console.error("Error deleting event after WordPress failure:", retryError);
                        toast.error("Loeschen fehlgeschlagen");
                    }
                }
            } else {
                console.error("Error deleting event:", error);
                toast.error("Loeschen fehlgeschlagen");
            }
        } finally {
            setDeletingEventId(null);
            setRejectionNotes(prev => {
                const next = { ...prev };
                delete next[eventId];
                return next;
            });
        }
    };

    const handleAssignmentChange = (eventId, email) => {
        setAssignmentSelections((prev) => ({
            ...prev,
            [eventId]: email
        }));
    };

    const handleAssignEvent = async (event) => {
        const selectedEmail = assignmentSelections[event.id] || "";
        const currentEmail = (event.created_by_email || "").toLowerCase();
        if (!selectedEmail || selectedEmail === currentEmail) {
            toast.error("Bitte einen anderen Veranstalter auswählen.");
            return;
        }
        try {
            setAssignmentSavingId(event.id);
            await apiService.assignEvent(event.id, selectedEmail);
            toast.success("Veranstalter zugewiesen");
            fetchPendingEvents();
        } catch (error) {
            console.error("Error assigning event:", error);
            toast.error(error.response?.data?.detail || "Zuweisung fehlgeschlagen");
        } finally {
            setAssignmentSavingId(null);
        }
    };

    const handleGemaExport = async (event, includeNonRequired = false) => {
        try {
            setGemaExportingId(event.id);
            const response = await apiService.exportGemaSetlistXlsx(event.id, includeNonRequired);
            const blob = new Blob(
                [response.data],
                { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
            );
            const contentDisposition = response.headers?.["content-disposition"] || "";
            const filenameMatch = contentDisposition.match(/filename=\"([^\"]+)\"/i);
            const filename = filenameMatch?.[1] || `gema_setlist_${event.id}.xlsx`;
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(downloadUrl);
            toast.success("GEMA Setlist Export erstellt");
        } catch (error) {
            console.error("Error exporting GEMA setlist:", error);
            toast.error(error.response?.data?.detail || "GEMA Export fehlgeschlagen");
        } finally {
            setGemaExportingId(null);
        }
    };

    const handleDispatchGemaReminders = async () => {
        try {
            setGemaDispatching(true);
            const result = await apiService.dispatchGemaReminders();
            toast.success(
                `GEMA-Reminder geprüft: ${result.events_checked || 0} Events, ${result.emails_sent || 0} Mails versendet.`
            );
        } catch (error) {
            console.error("Error dispatching GEMA reminders:", error);
            toast.error(error.response?.data?.detail || "GEMA-Reminder konnten nicht ausgelöst werden");
        } finally {
            setGemaDispatching(false);
        }
    };

    const handleRoleChange = async (userId, nextRole) => {
        try {
            setUpdatingUserId(userId);
            await apiService.updateUserRole(userId, nextRole);
            toast.success("Benutzerrolle aktualisiert");
            await fetchUsers();
        } catch (error) {
            console.error("Error updating user role:", error);
            toast.error("Benutzerrolle konnte nicht aktualisiert werden");
        } finally {
            setUpdatingUserId(null);
        }
    };

    const handleSectionToggle = (key) => {
        const nextOpen = !openSections[key];
        setOpenSections((prev) => ({ ...prev, [key]: nextOpen }));
        if (nextOpen) {
            setTimeout(() => {
                document.getElementById(`admin-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 0);
        }
    };

    const renderUsersSection = () => {
        if (!openSections.users) {
            return null;
        }
        if (usersLoading) {
            return (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600"></div>
                </div>
            );
        }
        if (users.length === 0) {
            return (
                <div className="text-center py-12">
                    <p className="text-gray-600 text-lg">Noch keine Benutzer angemeldet</p>
                    <p className="text-gray-500 text-sm mt-2">Sobald sich jemand anmeldet, erscheint er hier.</p>
                </div>
            );
        }
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                E-Mail
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Rolle
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Aktion
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                            <tr key={user.user_id}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                    {user.name || "Unbekannter Benutzer"}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                                <td className="px-4 py-3 text-sm">
                                    <span
                                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                                            user.role === "admin"
                                                ? "bg-green-100 text-green-800"
                                                : "bg-gray-100 text-gray-700"
                                        }`}
                                    >
                                        {user.role === "admin" ? "Administrator" : "Veranstalter"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleRoleChange(user.user_id, "admin")}
                                            disabled={user.role === "admin" || updatingUserId === user.user_id}
                                            className={`px-3 py-1 rounded-lg text-sm font-medium border transition ${
                                                user.role === "admin"
                                                    ? "bg-green-600 text-white border-green-600 cursor-not-allowed"
                                                    : "border-green-600 text-green-700 hover:bg-green-50"
                                            }`}
                                        >
                                            {updatingUserId === user.user_id && user.role !== "admin" ? "..." : "Admin"}
                                        </button>
                                        <button
                                            onClick={() => handleRoleChange(user.user_id, "organizer")}
                                            disabled={user.role === "organizer" || updatingUserId === user.user_id}
                                            className={`px-3 py-1 rounded-lg text-sm font-medium border transition ${
                                                user.role === "organizer"
                                                    ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                                                    : "border-gray-400 text-gray-700 hover:bg-gray-50"
                                            }`}
                                        >
                                            {updatingUserId === user.user_id && user.role !== "organizer" ? "..." : "Entfernen"}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };


    const handleSettingsToggle = (event) => {
        const enabled = event.target.checked;
        setNotificationSettings((prev) => ({
            ...prev,
            enabled,
            frequency: enabled && prev.frequency === "off" ? "immediate" : enabled ? prev.frequency : "off"
        }));
    };

    const handleSettingsChange = (field, value) => {
        setNotificationSettings((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveSettings = async (event) => {
        event.preventDefault();
        try {
            setSettingsSaving(true);
            const payload = {
                enabled: notificationSettings.enabled,
                frequency: notificationSettings.frequency,
                email: notificationSettings.email.trim() || null
            };
            const data = await apiService.updateNotificationSettings(payload);
            setNotificationSettings({
                enabled: Boolean(data.enabled),
                frequency: data.frequency || "off",
                email: data.email || ""
            });
            toast.success("Benachrichtigungseinstellungen gespeichert");
        } catch (error) {
            console.error("Error saving notification settings:", error);
            toast.error("Benachrichtigungseinstellungen konnten nicht gespeichert werden");
        } finally {
            setSettingsSaving(false);
        }
    };

    const handleScreenSettingsChange = (field, value) => {
        setScreenSettings((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveScreenSettings = async (event) => {
        event.preventDefault();
        try {
            setScreenSettingsSaving(true);
            const payload = {
                mode: screenSettings.mode,
                start_date: screenSettings.start_date || null,
                end_date: screenSettings.end_date || null
            };
            const data = await apiService.updateScreenSettings(payload);
            setScreenSettings({
                mode: data.mode || "all",
                start_date: data.start_date || "",
                end_date: data.end_date || ""
            });
            toast.success("Screen-Einstellungen gespeichert");
        } catch (error) {
            console.error("Error saving screen settings:", error);
            const message = error.response?.data?.detail || "Screen-Einstellungen konnten nicht gespeichert werden";
            toast.error(message);
        } finally {
            setScreenSettingsSaving(false);
        }
    };

    const handleCopyScreenUrl = async () => {
        try {
            await navigator.clipboard.writeText(screenUrl);
            toast.success("Screen-Link kopiert");
        } catch (error) {
            console.error("Failed to copy screen url:", error);
            toast.error("Link konnte nicht kopiert werden");
        }
    };

    const handleTestEmail = async () => {
        try {
            setTestSending(true);
            const recipient = notificationSettings.email.trim() || null;
            const data = await apiService.sendNotificationTest(recipient);
            setTestResult(data);
            if (data.sent) {
                toast.success(`Testmail gesendet an ${data.recipient}`);
            } else {
                const message = data.error || "Testmail fehlgeschlagen";
                toast.error(message);
            }
        } catch (error) {
            console.error("Error sending test email:", error);
            const message = error.response?.data?.detail || "Testmail fehlgeschlagen";
            toast.error(message);
        } finally {
            setTestSending(false);
        }
    };

    const handleStartSmtpOauth = async () => {
        try {
            setSmtpOauthError("");
            setSmtpOauthLoading(true);
            const data = await apiService.startSmtpOauthDeviceCode();
            setSmtpOauthDevice(data);
            toast.success("Device-Code erstellt. Bitte verifizieren.");
        } catch (error) {
            console.error("Error starting SMTP OAuth device flow:", error);
            const message = error.response?.data?.detail || "Device-Code konnte nicht erstellt werden";
            setSmtpOauthError(message);
            toast.error(message);
        } finally {
            setSmtpOauthLoading(false);
        }
    };

    const handlePollSmtpOauth = async () => {
        if (!smtpOauthDevice?.device_code) {
            return;
        }
        try {
            setSmtpOauthError("");
            setSmtpOauthPolling(true);
            const data = await apiService.pollSmtpOauthDeviceCode(smtpOauthDevice.device_code);
            if (data.status === "success") {
                toast.success("Outlook OAuth verbunden");
                setSmtpOauthDevice(null);
                await fetchSmtpOauthStatus();
                return;
            }
            if (data.status === "pending") {
                setSmtpOauthError("Noch nicht bestaetigt. Bitte im Browser abschliessen.");
                return;
            }
            if (data.status === "slow_down") {
                setSmtpOauthError("Bitte kurz warten und erneut pruefen.");
                return;
            }
            if (data.status === "expired") {
                setSmtpOauthError("Device-Code abgelaufen. Bitte neu starten.");
                setSmtpOauthDevice(null);
                return;
            }
            if (data.status === "denied") {
                setSmtpOauthError("Zugriff abgelehnt.");
                setSmtpOauthDevice(null);
                return;
            }
            setSmtpOauthError(data.detail || "Unbekannter OAuth-Status");
        } catch (error) {
            console.error("Error polling SMTP OAuth device flow:", error);
            const message = error.response?.data?.detail || "OAuth-Pruefung fehlgeschlagen";
            setSmtpOauthError(message);
            toast.error(message);
        } finally {
            setSmtpOauthPolling(false);
        }
    };

    const handleNoticeModeChange = (value) => {
        setNoticeMode(value);
        if (value === "month") {
            setNoticeMonths([noticeMonthSelection]);
        } else if (value === "quarter") {
            setNoticeMonths(resolveQuarterMonths(noticeQuarter));
        }
    };

    const handleNoticeMonthChange = (value) => {
        setNoticeMonthSelection(value);
        if (noticeMode === "month") {
            setNoticeMonths([value]);
        }
    };

    const handleNoticeQuarterChange = (value) => {
        setNoticeQuarter(value);
        if (noticeMode === "quarter") {
            setNoticeMonths(resolveQuarterMonths(value));
        }
    };

    const handleNoticeRangeChange = (startValue, endValue) => {
        setNoticeRangeStart(startValue);
        setNoticeRangeEnd(endValue);
        if (noticeMode === "range") {
            const start = parseDateInputValue(startValue);
            const end = parseDateInputValue(endValue);
            if (!start || !end) {
                toast.error("Bitte einen gültigen Zeitraum auswählen");
            }
        }
    };

    const buildNoticePayload = (send = false, includeContent = true, previewControls = false, forceSubject = false) => ({
        month: noticeMode === "range" ? null : (noticeMonths.length === 1 ? noticeMonths[0] : null),
        months: noticeMode === "range" ? null : noticeMonths,
        range_start: noticeMode === "range" ? noticeRangeStart : null,
        range_end: noticeMode === "range" ? noticeRangeEnd : null,
        year: noticeYear,
        recipient: send ? (noticeRecipient.trim() || null) : null,
        subject: includeContent
            ? ((forceSubject || noticeSubjectTouched) ? (noticeSubject.trim() || null) : null)
            : null,
        body: includeContent ? (noticeBody.trim() || null) : null,
        html_body: includeContent ? (noticeHtml.trim() || null) : null,
        exclude_event_ids: noticeExcludedIds,
        preview_controls: previewControls,
        send
    });

    const buildFlyerHtml = (events) => {
        const winterjazzColor = "#1c5a9e";
        const blocks = [];

        events.forEach((event, index) => {
            const title = normalizeFlyerText(event.title);
            const category = normalizeFlyerText(decodeHtml(event.category || ""));
            const shortDescription = formatFlyerShortDescription(event.short_description);
            const homepage = normalizeFlyerText(event.homepage_url || event.ticket_url);
            const doorTime = formatFlyerTime(event.door_time);
            const timeLabel = formatFlyerTime(event.time);
            const tiers = Array.isArray(event.pricing_tiers) ? event.pricing_tiers : [];
            const tiersLabel = tiers
                .map((tier) => {
                    const label = normalizeFlyerText(tier.label);
                    const price = normalizeFlyerText(tier.price);
                    if (!label && !price) {
                        return "";
                    }
                    if (!label) {
                        return price;
                    }
                    if (!price) {
                        return label;
                    }
                    return `${label}: ${price}`;
                })
                .filter(Boolean)
                .join(" / ");
            const admission = normalizeFlyerText(event.admission);
            const priceLabelRaw = tiersLabel || admission;
            const hasEuro = priceLabelRaw.includes("€");
            const hasEntryWord = /eintritt/i.test(priceLabelRaw);
            const priceLabel = priceLabelRaw
                ? (hasEuro || hasEntryWord ? priceLabelRaw : `Eintritt ${priceLabelRaw}`)
                : "";
            const infoLine = [timeLabel, priceLabel].filter(Boolean).join(" ");

            const isWinterjazz = /winterjazz/i.test(title);

            if (isWinterjazz && (index === 0 || !/winterjazz/i.test(normalizeFlyerText(events[index - 1]?.title)))) {
                blocks.push(
                    `<p style="margin:0 0 6pt 0; color:${winterjazzColor}; font-weight:bold;">WINTERJAZZ</p>`
                );
            }

            const colorStyle = isWinterjazz ? `color:${winterjazzColor};` : "";
            blocks.push(
                `<p style="margin:0 0 2pt 0; font-weight:bold; ${colorStyle}">${escapeFlyerHtml(formatFlyerDate(event.date))}</p>`
            );
            if (title || category) {
                const titleLine = [title, category].filter(Boolean).join(" - ");
                blocks.push(
                    `<p style="margin:0 0 2pt 0; font-weight:bold; ${colorStyle}">${escapeFlyerHtml(titleLine)}</p>`
                );
            }
            if (shortDescription) {
                blocks.push(
                    `<p style="margin:0 0 2pt 0; ${colorStyle}">${escapeFlyerHtml(shortDescription).replace(/\n/g, "<br>")}</p>`
                );
            }
            if (homepage) {
                blocks.push(
                    `<p style="margin:0 0 2pt 0; ${colorStyle}">${escapeFlyerHtml(homepage)}</p>`
                );
            }
            if (doorTime) {
                blocks.push(
                    `<p style="margin:0 0 2pt 0; ${colorStyle}">Einlass: ${escapeFlyerHtml(doorTime)}</p>`
                );
            }
            if (infoLine) {
                blocks.push(
                    `<p style="margin:0 0 8pt 0; ${colorStyle}">${escapeFlyerHtml(infoLine)}</p>`
                );
            } else {
                blocks.push(`<p style="margin:0 0 8pt 0;"></p>`);
            }

        });

        const footer = [
            "Seit 2022 betreibt der Kultdiak Stuttgart e.V. im Bunker unter dem Diakonissenplatz ein Kulturzentrum:",
            "KULTUR IM BUNKER. Zweck des Vereins ist die Förderung und Unterstützung von Kunst und Kultur durch",
            "sozio-kulturelle Veranstaltungen in Stuttgart, vor allem auf dem Gebiet der darstellenden Kunst, der Musik,",
            "des Tanzes, des Theaters und der Literatur.",
            "Wir wollen hier unterschiedlich kulturelle Strömungen, die mit unseren Gegebenheiten klarkommen,",
            "einbinden. Wichtig ist uns außerdem, Bürger*Innen aus der direkten Umgebung eine Mitwirkung und",
            "Anteilnahme an unserem Kulturzentrum zu ermöglichen."
        ];

        const footerHtml = footer
            .map((line) => `<p style="margin:0 0 2pt 0;">${escapeFlyerHtml(line)}</p>`)
            .join("");

        return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Flyer Export</title>
</head>
<body style="font-family: Arial, sans-serif; font-size: 11pt; color: #000;">
${blocks.join("")}
<p style="margin:6pt 0;"></p>
${footerHtml}
</body>
</html>`;
    };

    const getFlyerEvents = () => {
        const filtered = pendingEvents
            .map((event) => {
                const entries = getFlyerScheduleEntries(event);
                const matchingEntries = entries.filter((entry) => {
                    const parsed = parseEventDate(entry.date);
                    if (!parsed) {
                        return false;
                    }
                    if (noticeMode === "range") {
                        const start = parseDateInputValue(noticeRangeStart);
                        const end = parseDateInputValue(noticeRangeEnd);
                        if (!start || !end) {
                            return false;
                        }
                        const startIndex = Date.UTC(start.year, start.month - 1, start.day);
                        const endIndex = Date.UTC(end.year, end.month - 1, end.day);
                        const rangeStart = Math.min(startIndex, endIndex);
                        const rangeEnd = Math.max(startIndex, endIndex);
                        const eventIndex = Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
                        return eventIndex >= rangeStart && eventIndex <= rangeEnd;
                    }
                    if (!noticeMonths.includes(parsed.getMonth() + 1)) {
                        return false;
                    }
                    return parsed.getFullYear() === Number(noticeYear);
                });
                const firstMatchingEntry = matchingEntries.sort((a, b) => {
                    const aIndex = getFlyerDateIndex(a.date);
                    const bIndex = getFlyerDateIndex(b.date);
                    if (aIndex !== null && bIndex !== null && aIndex !== bIndex) {
                        return aIndex - bIndex;
                    }
                    return (parseFlyerTimeMinutes(a.time) ?? 99999) - (parseFlyerTimeMinutes(b.time) ?? 99999);
                })[0];
                return firstMatchingEntry
                    ? {
                        ...event,
                        date: firstMatchingEntry.date,
                        time: firstMatchingEntry.time,
                        door_time: firstMatchingEntry.door_time ?? event.door_time,
                        end_time: firstMatchingEntry.end_time ?? event.end_time
                    }
                    : null;
            })
            .filter(Boolean)
            .filter((event) => !noticeExcludedIds.includes(event.id));

        return filtered.sort((a, b) => {
            const aDate = parseEventDate(a.date);
            const bDate = parseEventDate(b.date);
            if (aDate && bDate) {
                const diff = aDate.getTime() - bDate.getTime();
                if (diff !== 0) {
                    return diff;
                }
            } else if (aDate) {
                return -1;
            } else if (bDate) {
                return 1;
            }
            const aMinutes = parseFlyerTimeMinutes(a.time);
            const bMinutes = parseFlyerTimeMinutes(b.time);
            if (aMinutes !== null && bMinutes !== null) {
                return aMinutes - bMinutes;
            }
            if (aMinutes !== null) {
                return -1;
            }
            if (bMinutes !== null) {
                return 1;
            }
            return String(a.time || "").localeCompare(String(b.time || ""), "de-DE");
        });
    };

    const downloadFlyerDoc = (events) => {
        const content = buildFlyerHtml(events);
        const blob = new Blob([content], { type: "application/msword" });
        const monthLabel = noticeMode === "range"
            ? formatNoticeRangeForFilename(noticeRangeStart, noticeRangeEnd)
            : (formatNoticeMonthsForFilename(noticeMonths) || "monate");
        const filename = noticeMode === "range"
            ? `Flyer_${monthLabel}.doc`
            : `Flyer_${noticeYear}_${monthLabel}.doc`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const handleFlyerExport = async () => {
        try {
            setFlyerExporting(true);
            const events = getFlyerEvents();
            if (events.length === 0) {
                toast.error("Keine Veranstaltungen fuer den Flyer gefunden");
                return;
            }
            downloadFlyerDoc(events);
            toast.success("Flyer-Export erstellt");
        } catch (error) {
            console.error("Error exporting flyer:", error);
            toast.error("Flyer-Export fehlgeschlagen");
        } finally {
            setFlyerExporting(false);
        }
    };

    const handleGenerateNotice = async () => {
        try {
            setNoticeLoading(true);
            setNoticeExcludedIds([]);
            const data = await apiService.createEventNotice(buildNoticePayload(false, false, false));
            if (!noticeSubjectTouched) {
                setNoticeAutoSubject(data.subject || "");
                setNoticeSubject(data.subject || "");
            }
            setNoticeBody(data.body || "");
            setNoticeHtml(data.html_body || "");
            setNoticeEventsCount(data.events_count ?? null);
            setNoticeEvents(Array.isArray(data.events) ? data.events : []);
            setNoticeGenerated(true);
            toast.success("Veranstaltungshinweise erstellt");
        } catch (error) {
            console.error("Error generating event notice:", error);
            toast.error(getApiErrorMessage(error, "Veranstaltungshinweise konnten nicht erstellt werden"));
        } finally {
            setNoticeLoading(false);
        }
    };

    const handleSendNotice = async () => {
        try {
            setNoticeSending(true);
            const data = await apiService.createEventNotice(buildNoticePayload(true, true, false));
            if (!noticeSubjectTouched) {
                setNoticeAutoSubject(data.subject || "");
                setNoticeSubject(data.subject || "");
            }
            setNoticeBody(data.body || "");
            setNoticeHtml(data.html_body || "");
            setNoticeEventsCount(data.events_count ?? null);
            setNoticeEvents(Array.isArray(data.events) ? data.events : []);
            setNoticeGenerated(true);
            toast.success("Veranstaltungshinweise wurden versendet");
        } catch (error) {
            console.error("Error sending event notice:", error);
            toast.error(getApiErrorMessage(error, "Veranstaltungshinweise konnten nicht versendet werden"));
        } finally {
            setNoticeSending(false);
        }
    };

    const handleCopyNotice = async () => {
        if (!noticeHtml && !noticeBody) {
            toast.error("Bitte zuerst die Hinweise generieren");
            return;
        }
        try {
            await navigator.clipboard.writeText(noticeHtml || noticeBody);
            toast.success("HTML in die Zwischenablage kopiert");
        } catch (error) {
            console.error("Error copying event notice:", error);
            toast.error("Kopieren fehlgeschlagen");
        }
    };

    const categoryOptions = useMemo(() => {
        const values = new Set();
        pendingEvents.forEach((event) => {
            const category = decodeHtml(event.category || "").trim();
            if (category) {
                values.add(category);
            }
        });
        return Array.from(values).sort((a, b) => a.localeCompare(b, "de-DE"));
    }, [pendingEvents]);

    const yearOptions = useMemo(() => {
        const values = new Set();
        pendingEvents.forEach((event) => {
            const parsed = parseEventDate(event.date);
            if (parsed) {
                values.add(parsed.getFullYear());
            }
        });
        return Array.from(values).sort((a, b) => b - a);
    }, [pendingEvents]);

    const selectedNoticeLabel = useMemo(() => (
        noticeMode === "range"
            ? formatNoticeRangeLabel(noticeRangeStart, noticeRangeEnd)
            : formatNoticeMonthsLabel(noticeMonths)
    ), [noticeMode, noticeRangeStart, noticeRangeEnd, noticeMonths]);

    const defaultNoticeSubject = useMemo(() => {
        if (noticeMode === "range") {
            return selectedNoticeLabel
                ? `Veranstaltungshinweise ${selectedNoticeLabel}`
                : "Veranstaltungshinweise";
        }
        const label = formatNoticeMonthsLabel(noticeMonths);
        return label
            ? `Veranstaltungshinweise ${label} ${noticeYear}`
            : `Veranstaltungshinweise ${noticeYear}`;
    }, [noticeMode, selectedNoticeLabel, noticeMonths, noticeYear]);

    const organizerOptions = useMemo(() => {
        return (users || [])
            .filter((user) => Boolean(user.email))
            .sort((a, b) => {
                const nameA = (a.name || a.email || "").toLowerCase();
                const nameB = (b.name || b.email || "").toLowerCase();
                return nameA.localeCompare(nameB);
            });
    }, [users]);

    const toggleNoticeExclude = (eventId) => {
        setNoticeExcludedIds((prev) => (
            prev.includes(eventId)
                ? prev.filter((id) => id !== eventId)
                : [...prev, eventId]
        ));
    };

    const filteredEvents = useMemo(() => {
        const searchTerm = eventFilters.search.trim().toLowerCase();
        return pendingEvents.filter((event) => {
            const status = (event.status || "").trim().toLowerCase();
            const isImported = event.imported_from_wp === true;
            if (eventFilters.status !== "all") {
                if (eventFilters.status === "wordpress_error") {
                    if (!hasWordpressIssue(event)) {
                        return false;
                    }
                } else if (eventFilters.status === "approved") {
                    if (isImported) {
                        return false;
                    }
                    if (status !== "approved" && status !== "published") {
                        return false;
                    }
                } else if (status !== eventFilters.status) {
                    return false;
                }
            }
            if (eventFilters.category !== "all") {
                const category = decodeHtml(event.category || "").trim();
                if (category !== eventFilters.category) {
                    return false;
                }
            }
            if (eventFilters.month !== "all" || eventFilters.year !== "all") {
                const parsed = parseEventDate(event.date);
                if (!parsed) {
                    return false;
                }
                if (eventFilters.month !== "all" && parsed.getMonth() + 1 !== Number(eventFilters.month)) {
                    return false;
                }
                if (eventFilters.year !== "all" && parsed.getFullYear() !== Number(eventFilters.year)) {
                    return false;
                }
            }
            if (searchTerm) {
                const haystack = [
                    event.title,
                    event.created_by_name,
                    event.created_by_email,
                    event.id,
                    decodeHtml(event.category || "")
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                if (!haystack.includes(searchTerm)) {
                    return false;
                }
            }
            return true;
        });
    }, [pendingEvents, eventFilters]);

    const sortedEvents = useMemo(() => {
        const events = [...filteredEvents];
        const compareEventDates = (aValue, bValue) => {
            const aDate = parseEventDate(aValue);
            const bDate = parseEventDate(bValue);
            if (!aDate && !bDate) {
                return 0;
            }
            if (!aDate) {
                return 1;
            }
            if (!bDate) {
                return -1;
            }
            return aDate.getTime() - bDate.getTime();
        };
        const compareCreatedDates = (aValue, bValue) => {
            const aDate = aValue ? new Date(aValue) : null;
            const bDate = bValue ? new Date(bValue) : null;
            if (!aDate || Number.isNaN(aDate.getTime())) {
                return 1;
            }
            if (!bDate || Number.isNaN(bDate.getTime())) {
                return -1;
            }
            return aDate.getTime() - bDate.getTime();
        };
        events.sort((a, b) => {
            switch (eventSort) {
                case "date-asc":
                    return compareEventDates(a.date, b.date);
                case "date-desc":
                    return compareEventDates(b.date, a.date);
                case "created-asc":
                    return compareCreatedDates(a.created_at, b.created_at);
                case "created-desc":
                    return compareCreatedDates(b.created_at, a.created_at);
                case "title-desc":
                    return (b.title || "").localeCompare(a.title || "", "de-DE");
                case "title-asc":
                default:
                    return (a.title || "").localeCompare(b.title || "", "de-DE");
            }
        });
        return events;
    }, [filteredEvents, eventSort]);

    const gemaEvents = useMemo(
        () => pendingEvents.filter((event) => Boolean(event.gema_relevant)),
        [pendingEvents]
    );
    const gemaRequiredEvents = useMemo(
        () => gemaEvents.filter((event) => Boolean(event.is_setlist_required)),
        [gemaEvents]
    );
    const gemaMissingSetlistCount = useMemo(
        () => gemaRequiredEvents.filter((event) => event.setlist_status !== "Eingereicht").length,
        [gemaRequiredEvents]
    );
    const gemaApiEntries = useMemo(() => {
        const rawEntries = Array.isArray(gemaApiData.lizenzListe) ? [...gemaApiData.lizenzListe] : [];
        const byDateDesc = rawEntries.sort((a, b) => {
            const aDate = parseEventDate(a?.eventDatumBeginn);
            const bDate = parseEventDate(b?.eventDatumBeginn);
            if (!aDate && !bDate) {
                return 0;
            }
            if (!aDate) {
                return 1;
            }
            if (!bDate) {
                return -1;
            }
            return bDate.getTime() - aDate.getTime();
        });
        const activeEntries = gemaApiShowCancelled
            ? byDateDesc
            : byDateDesc.filter((entry) => entry?.status !== "STORNIERT");
        if (gemaApiFilter === GEMA_API_FILTERS.MISSING) {
            return activeEntries.filter((entry) => String(entry?.musikfolgeStatus || "") === "0");
        }
        if (gemaApiFilter === GEMA_API_FILTERS.DONE) {
            return activeEntries.filter((entry) => {
                const status = String(entry?.musikfolgeStatus || "");
                return status === "2" || status === "3";
            });
        }
        return activeEntries;
    }, [gemaApiData.lizenzListe, gemaApiFilter, gemaApiShowCancelled]);
    const gemaApiMissingCount = useMemo(
        () => (Array.isArray(gemaApiData.lizenzListe) ? gemaApiData.lizenzListe : [])
            .filter((entry) => String(entry?.musikfolgeStatus || "") === "0")
            .length,
        [gemaApiData.lizenzListe]
    );
    const gemaApiDoneCount = useMemo(
        () => (Array.isArray(gemaApiData.lizenzListe) ? gemaApiData.lizenzListe : [])
            .filter((entry) => {
                const status = String(entry?.musikfolgeStatus || "");
                return status === "2" || status === "3";
            })
            .length,
        [gemaApiData.lizenzListe]
    );

    const totalPages = Math.max(1, Math.ceil(sortedEvents.length / eventPageSize));
    const eventPageSafe = Math.min(eventPage, totalPages);
    const pageStart = (eventPageSafe - 1) * eventPageSize;
    const pageEnd = pageStart + eventPageSize;
    const pagedEvents = sortedEvents.slice(pageStart, pageEnd);

    useEffect(() => {
        if (eventPageSafe !== eventPage) {
            setEventPage(eventPageSafe);
        }
    }, [eventPageSafe, eventPage]);

    const noticeMonthsKey = `${noticeMode}:${noticeMonths.join(",")}:${noticeRangeStart}:${noticeRangeEnd}`;

    useEffect(() => {
        if (!noticeSubjectTouched) {
            setNoticeSubject(defaultNoticeSubject);
            setNoticeAutoSubject(defaultNoticeSubject);
        }
    }, [defaultNoticeSubject, noticeSubjectTouched]);

    useEffect(() => {
        if (!noticeGenerated) {
            return;
        }
        const timer = setTimeout(async () => {
            try {
                setNoticePreviewLoading(true);
                const payload = {
                    ...buildNoticePayload(false, true, false, true),
                    html_body: null
                };
                const data = await apiService.createEventNotice(payload);
                setNoticeHtml(data.html_body || "");
                setNoticeEventsCount(data.events_count ?? null);
                setNoticeEvents(Array.isArray(data.events) ? data.events : []);
            } catch (error) {
                console.error("Error updating event notice preview:", error);
            } finally {
                setNoticePreviewLoading(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [noticeBody, noticeSubject, noticeMonthsKey, noticeYear, noticeGenerated, noticeExcludedIds]);


    if (eventsLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white shadow-lg">
                <h2 className="text-3xl font-bold mb-2">Admin Panel</h2>
                <p className="text-purple-100">Veranstaltungen prüfen und genehmigen</p>
            </div>

            <AdminTabs />

            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200 flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={() => handleSectionToggle("notifications")}
                    className="rounded-full border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-100"
                >
                    Admin-Benachrichtigungen {openSections.notifications ? "▼" : "▶"}
                </button>
                <button
                    type="button"
                    onClick={() => handleSectionToggle("screen")}
                    className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-100"
                >
                    Screen-Show {openSections.screen ? "▼" : "▶"}
                </button>
                <button
                    type="button"
                    onClick={() => handleSectionToggle("notice")}
                    className="rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                >
                    Hinweise {openSections.notice ? "▼" : "▶"}
                </button>
                <button
                    type="button"
                    onClick={() => handleSectionToggle("deadlines")}
                    className="rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
                >
                    Deadlines {openSections.deadlines ? "▼" : "▶"}
                </button>
                <button
                    type="button"
                    onClick={() => handleSectionToggle("events")}
                    className="rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100"
                >
                    Veranstaltungen {openSections.events ? "▼" : "▶"}
                </button>
                <button
                    type="button"
                    onClick={() => handleSectionToggle("users")}
                    className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
                >
                    Benutzerrechte {openSections.users ? "▼" : "▶"}
                </button>
            </div>

            <div id="admin-notifications" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Admin-Benachrichtigungen</h3>
                        <p className="text-sm text-gray-500">Einstellungen für neue Veranstaltungen</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleSectionToggle("notifications")}
                        className="text-sm font-semibold text-purple-600 hover:text-purple-700"
                    >
                        {openSections.notifications ? "Schliessen" : "Öffnen"}
                    </button>
                </div>
                {openSections.notifications && (settingsLoading ? (
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-purple-600"></div>
                        Einstellungen werden geladen...
                    </div>
                ) : (
                    <form className="space-y-4" onSubmit={handleSaveSettings}>
                        <label className="flex items-start gap-3 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={notificationSettings.enabled}
                                onChange={handleSettingsToggle}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <span>Benachrichtigungen aktivieren</span>
                        </label>

                        <div className="grid gap-4 md:grid-cols-2">
                            <label className="text-sm text-gray-700">
                                <span className="block mb-1 font-medium">Häufigkeit</span>
                                <select
                                    value={notificationSettings.frequency}
                                    onChange={(event) => handleSettingsChange("frequency", event.target.value)}
                                    disabled={!notificationSettings.enabled}
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100"
                                >
                                    <option value="immediate">Bei jeder neuen Veranstaltung</option>
                                    <option value="daily">Einmal pro Tag (Zusammenfassung)</option>
                                    <option value="off">Keine Benachrichtigungen</option>
                                </select>
                            </label>

                            <label className="text-sm text-gray-700">
                                <span className="block mb-1 font-medium">Empfänger-E-Mail</span>
                                <input
                                    type="email"
                                    value={notificationSettings.email}
                                    onChange={(event) => handleSettingsChange("email", event.target.value)}
                                    disabled={!notificationSettings.enabled}
                                    placeholder="name@beispiel.de"
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100"
                                />
                                <span className="mt-1 block text-xs text-gray-500">Leer lassen nutzt die Login-E-Mail.</span>
                            </label>
                        </div>

                        {smtpOauthStatus && (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
                                <p>
                                    Absender:{" "}
                                    {smtpOauthStatus.email_provider === "graph"
                                        ? (smtpOauthStatus.oauth_account_email || "OAuth-Konto aus Microsoft Graph")
                                        : (smtpOauthStatus.smtp_from || "nicht konfiguriert")}
                                </p>
                                {smtpOauthStatus.email_provider === "graph" && smtpOauthStatus.smtp_from && (
                                    <p>Konfigurierter From-Header: {smtpOauthStatus.smtp_from}</p>
                                )}
                            </div>
                        )}

                        <div className="flex flex-wrap items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleTestEmail}
                                disabled={testSending}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                                {testSending ? "Testen..." : "Test-Mail senden"}
                            </button>
                            <button
                                type="submit"
                                disabled={settingsSaving}
                                className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:opacity-60"
                            >
                                {settingsSaving ? "Speichern..." : "Speichern"}
                            </button>
                        </div>
                        {testResult && (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
                                <p>
                                    Ergebnis: {testResult.sent ? "erfolgreich" : "fehlgeschlagen"} | Empfaenger: {testResult.recipient}
                                </p>
                                <p>
                                    SMTP: {testResult.smtp_host}:{testResult.smtp_port} | SSL: {String(testResult.smtp_use_ssl)} | TLS: {String(testResult.smtp_use_tls)}
                                </p>
                                {!testResult.sent && testResult.error && (
                                    <p>Fehler: {testResult.error}</p>
                                )}
                            </div>
                        )}
                        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Outlook OAuth (SMTP)</p>
                                    <p className="text-xs text-gray-500">Device-Code Flow fuer Refresh-Token.</p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={handleStartSmtpOauth}
                                        disabled={smtpOauthLoading}
                                        className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                    >
                                        {smtpOauthLoading ? "Lade..." : "Device-Code starten"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePollSmtpOauth}
                                        disabled={smtpOauthPolling || !smtpOauthDevice}
                                        className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
                                    >
                                        {smtpOauthPolling ? "Pruefe..." : "Token abrufen"}
                                    </button>
                                </div>
                            </div>
                            {smtpOauthStatus && (
                                <div className="mt-3 text-xs text-gray-600">
                                    <p>
                                        Status: {smtpOauthStatus.has_refresh_token ? "verbunden" : "nicht verbunden"} | Auth: {smtpOauthStatus.auth_method}
                                    </p>
                                    <p>
                                        Client-ID: {smtpOauthStatus.client_id_set ? "gesetzt" : "fehlt"} | Client-Secret: {smtpOauthStatus.client_secret_set ? "gesetzt" : "fehlt"}
                                    </p>
                                    {smtpOauthStatus.updated_at && (
                                        <p>Letzte Aktualisierung: {formatDateTime(smtpOauthStatus.updated_at)}</p>
                                    )}
                                </div>
                            )}
                            {smtpOauthDevice && (
                                <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
                                    <p className="font-semibold">Code: {smtpOauthDevice.user_code}</p>
                                    <p className="mt-1">
                                        <button
                                            type="button"
                                            onClick={() => window.open(smtpOauthDevice.verification_uri, "_blank", "noopener,noreferrer")}
                                            className="text-blue-700 underline"
                                        >
                                            {smtpOauthDevice.verification_uri}
                                        </button>
                                    </p>
                                    {smtpOauthDevice.message && (
                                        <p className="mt-2 text-blue-800">{smtpOauthDevice.message}</p>
                                    )}
                                </div>
                            )}
                            {smtpOauthError && (
                                <p className="mt-2 text-xs text-rose-600">{smtpOauthError}</p>
                            )}
                        </div>
                    </form>
                ))}
            </div>

            <div id="admin-screen" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Screen-Show</h3>
                        <p className="text-sm text-gray-500">Welche Events im Werbescreen laufen</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleSectionToggle("screen")}
                        className="text-sm font-semibold text-orange-600 hover:text-orange-700"
                    >
                        {openSections.screen ? "Schliessen" : "Öffnen"}
                    </button>
                </div>
                {openSections.screen && (screenSettingsLoading ? (
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-orange-500"></div>
                        Screen-Einstellungen werden geladen...
                    </div>
                ) : (
                    <form className="space-y-4" onSubmit={handleSaveScreenSettings}>
                        <div className="grid gap-4 md:grid-cols-3">
                            <label className="text-sm text-gray-700">
                                <span className="block mb-1 font-medium">Zeitraum</span>
                                <select
                                    value={screenSettings.mode}
                                    onChange={(event) => handleScreenSettingsChange("mode", event.target.value)}
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                                >
                                    <option value="all">Alle veröffentlichten</option>
                                    <option value="next_week">Nächste Woche</option>
                                    <option value="range">Zeitraum wählen</option>
                                </select>
                            </label>
                            {screenSettings.mode === "range" && (
                                <>
                                    <label className="text-sm text-gray-700">
                                        <span className="block mb-1 font-medium">Startdatum</span>
                                        <input
                                            type="date"
                                            value={screenSettings.start_date}
                                            onChange={(event) => handleScreenSettingsChange("start_date", event.target.value)}
                                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                                        />
                                    </label>
                                    <label className="text-sm text-gray-700">
                                        <span className="block mb-1 font-medium">Enddatum</span>
                                        <input
                                            type="date"
                                            value={screenSettings.end_date}
                                            onChange={(event) => handleScreenSettingsChange("end_date", event.target.value)}
                                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                                        />
                                    </label>
                                </>
                            )}
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                            <p className="font-semibold text-gray-900">Screen-Link</p>
                            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                                <input
                                    type="text"
                                    value={screenUrl}
                                    readOnly
                                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                                />
                                <button
                                    type="button"
                                    onClick={handleCopyScreenUrl}
                                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                >
                                    Kopieren
                                </button>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">Der Link übernimmt die aktuelle Filterauswahl.</p>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={screenSettingsSaving}
                                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 disabled:opacity-60"
                            >
                                {screenSettingsSaving ? "Speichern..." : "Speichern"}
                            </button>
                        </div>
                    </form>
                ))}
            </div>

            <div id="admin-notice" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Veranstaltungshinweise</h3>
                        <p className="text-sm text-gray-500">Mailtext für Veranstaltungslisten erstellen</p>
                    </div>
                    {noticeEventsCount !== null && (
                        <span className="text-sm text-gray-500">
                            {noticeEventsCount} Veranstaltungen gefunden
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => handleSectionToggle("notice")}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                    >
                        {openSections.notice ? "Schliessen" : "Öffnen"}
                    </button>
                </div>

                {openSections.notice && (
                <>
                <div className="grid gap-4 md:grid-cols-4">
                    <label className="text-sm text-gray-700">
                        <span className="block mb-1 font-medium">Zeitraum</span>
                        <select
                            value={noticeMode}
                            onChange={(event) => handleNoticeModeChange(event.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                            <option value="month">Einzelmonat</option>
                            <option value="quarter">Quartal</option>
                            <option value="range">Mehrere Monate (von/bis)</option>
                        </select>
                    </label>

                    {noticeMode === "month" && (
                    <label className="text-sm text-gray-700">
                        <span className="block mb-1 font-medium">Monat</span>
                        <select
                            value={noticeMonthSelection}
                            onChange={(event) => handleNoticeMonthChange(Number(event.target.value))}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                            {MONTH_NAMES.map((label, index) => (
                                <option key={label} value={index + 1}>{label}</option>
                            ))}
                        </select>
                    </label>
                    )}

                    {noticeMode === "quarter" && (
                    <label className="text-sm text-gray-700">
                        <span className="block mb-1 font-medium">Quartal</span>
                        <select
                            value={noticeQuarter}
                            onChange={(event) => handleNoticeQuarterChange(Number(event.target.value))}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                            <option value={1}>Q1 (Jan - Mär)</option>
                            <option value={2}>Q2 (Apr - Jun)</option>
                            <option value={3}>Q3 (Jul - Sep)</option>
                            <option value={4}>Q4 (Okt - Dez)</option>
                        </select>
                    </label>
                    )}

                    {noticeMode === "range" && (
                    <label className="text-sm text-gray-700">
                        <span className="block mb-1 font-medium">Von</span>
                        <input
                            type="date"
                            value={noticeRangeStart}
                            onChange={(event) => handleNoticeRangeChange(event.target.value, noticeRangeEnd)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                    </label>
                    )}

                    {noticeMode === "range" && (
                    <label className="text-sm text-gray-700">
                        <span className="block mb-1 font-medium">Bis</span>
                        <input
                            type="date"
                            value={noticeRangeEnd}
                            onChange={(event) => handleNoticeRangeChange(noticeRangeStart, event.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                    </label>
                    )}

                    {noticeMode !== "range" && (
                    <label className="text-sm text-gray-700">
                        <span className="block mb-1 font-medium">Jahr</span>
                        <input
                            type="number"
                            min="2000"
                            max="2100"
                            value={noticeYear}
                            onChange={(event) => setNoticeYear(Number(event.target.value))}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                    </label>
                    )}

                    <label className="text-sm text-gray-700">
                        <span className="block mb-1 font-medium">Empfänger-E-Mail</span>
                        <input
                            type="email"
                            value={noticeRecipient}
                            onChange={(event) => setNoticeRecipient(event.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                    </label>
                </div>

                {noticeMode === "range" && (
                    <p className="mt-3 text-xs text-gray-500">
                        Ausgewählt: {selectedNoticeLabel || "Keine"}
                    </p>
                )}

                <div className="mt-4 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={handleGenerateNotice}
                        disabled={noticeLoading}
                        className="w-full rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:opacity-60 sm:w-48"
                    >
                        {noticeLoading ? "Erstellen..." : "Hinweise erstellen"}
                    </button>
                    <button
                        type="button"
                        onClick={handleSendNotice}
                        disabled={noticeSending}
                        className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 sm:w-48"
                    >
                        {noticeSending ? "Senden..." : "E-Mail senden"}
                    </button>
                    <button
                        type="button"
                        onClick={handleCopyNotice}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 sm:w-48"
                    >
                        Text kopieren
                    </button>
                    <button
                        type="button"
                        onClick={handleFlyerExport}
                        disabled={flyerExporting}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 sm:w-48"
                    >
                        {flyerExporting ? "Flyer wird erstellt..." : "Flyer exportieren (Word)"}
                    </button>
                </div>

                <div className="mt-4 space-y-3">
                    <label className="text-sm text-gray-700">
                        <span className="block mb-1 font-medium">Betreff</span>
                        <input
                            type="text"
                            value={noticeSubject}
                            onChange={(event) => {
                                const nextValue = event.target.value;
                                setNoticeSubject(nextValue);
                                setNoticeSubjectTouched(nextValue.trim() !== defaultNoticeSubject.trim());
                            }}
                            placeholder="Betreff der E-Mail"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                    </label>
                </div>
                {noticeEvents.length > 0 && (
                    <div className="mt-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Veranstaltungen in der Vorschau</div>
                        <div className="space-y-2">
                            {noticeEvents.map((event) => {
                                const isExcluded = noticeExcludedIds.includes(event.id);
                                const dateLabel = event.date ? formatDate(event.date) : "";
                                const timeLabel = event.time ? ` • ${event.time}` : "";
                                return (
                                    <div
                                        key={event.id}
                                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                                            isExcluded ? "bg-gray-50 text-gray-400" : "bg-white text-gray-700"
                                        }`}
                                    >
                                        <div className={isExcluded ? "line-through" : ""}>
                                            <div className="font-medium">{event.title}</div>
                                            {(dateLabel || timeLabel) && (
                                                <div className="text-xs text-gray-500">{dateLabel}{timeLabel}</div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => toggleNoticeExclude(event.id)}
                                            className={`ml-3 inline-flex h-6 w-6 items-center justify-center rounded-full border ${
                                                isExcluded
                                                    ? "border-gray-300 text-gray-400 hover:text-gray-600"
                                                    : "border-red-200 text-red-500 hover:text-red-600"
                                            }`}
                                            title={isExcluded ? "Wieder aufnehmen" : "Ausschließen"}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className="mt-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                        Vorschau{noticePreviewLoading ? " (aktualisiert...)" : ""}
                    </div>
                    <div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                        <iframe
                            title="Veranstaltungshinweise Vorschau"
                            srcDoc={noticeHtml}
                            className="w-full h-[600px]"
                        />
                    </div>
                </div>
                </>
                )}
            </div>

            <div id="admin-deadlines" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Deadlines</h3>
                        <p className="text-sm text-gray-500">Pflichtfelder je Monat festlegen und Erinnerungen planen</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleSectionToggle("deadlines")}
                        className="text-sm font-semibold text-rose-600 hover:text-rose-700"
                    >
                        {openSections.deadlines ? "Schliessen" : "Öffnen"}
                    </button>
                </div>

                {openSections.deadlines && (
                <>
                <form className="space-y-4" onSubmit={handleDeadlineSubmit}>
                    <div className="grid gap-4 md:grid-cols-3">
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Monat</span>
                            <select
                                value={deadlineForm.month}
                                onChange={(event) => handleDeadlineChange("month", Number(event.target.value))}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                            >
                                {MONTH_NAMES.map((label, index) => (
                                    <option key={label} value={index + 1}>{label}</option>
                                ))}
                            </select>
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Jahr</span>
                            <input
                                type="number"
                                min="2000"
                                max="2100"
                                value={deadlineForm.year}
                                onChange={(event) => handleDeadlineChange("year", Number(event.target.value))}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                            />
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Deadline</span>
                            <input
                                type="datetime-local"
                                value={deadlineForm.dueAt}
                                onChange={(event) => handleDeadlineChange("dueAt", event.target.value)}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                            />
                            <span className="mt-1 block text-xs text-gray-500">Die Deadline muss vor dem Monatsbeginn liegen.</span>
                        </label>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Pflichtfelder für diesen Monat</p>
                        <div className="grid gap-2 md:grid-cols-2">
                            {DEADLINE_FIELDS.map((field) => (
                                <label key={field.value} className="flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={deadlineForm.requiredFields.includes(field.value)}
                                        onChange={() => handleDeadlineFieldToggle(field.value)}
                                        className="h-4 w-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                                    />
                                    <span>{field.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-3">
                        {editingDeadlineId && (
                            <button
                                type="button"
                                onClick={resetDeadlineForm}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                Abbrechen
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={deadlinesSaving}
                            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700 disabled:opacity-60"
                        >
                            {deadlinesSaving ? "Speichern..." : editingDeadlineId ? "Änderungen speichern" : "Deadline speichern"}
                        </button>
                    </div>
                </form>

                <div className="mt-6">
                    {deadlinesLoading ? (
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-rose-600"></div>
                            Deadlines werden geladen...
                        </div>
                    ) : deadlines.length === 0 ? (
                        <p className="text-sm text-gray-500">Noch keine Deadlines angelegt.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="text-xs uppercase text-gray-500 border-b">
                                    <tr>
                                        <th className="py-2 pr-4">Monat</th>
                                        <th className="py-2 pr-4">Deadline</th>
                                        <th className="py-2 pr-4">Pflichtfelder</th>
                                        <th className="py-2 pr-4">Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deadlines.map((deadline) => (
                                        <tr key={deadline.id} className="border-b last:border-none">
                                            <td className="py-3 pr-4 font-medium text-gray-900">
                                                {MONTH_NAMES[deadline.month - 1] || deadline.month} {deadline.year}
                                            </td>
                                            <td className="py-3 pr-4 text-gray-700">
                                                {formatDateTime(deadline.due_at)}
                                            </td>
                                            <td className="py-3 pr-4 text-gray-700">
                                                {(deadline.required_fields || [])
                                                    .map((field) => DEADLINE_FIELDS.find((entry) => entry.value === field)?.label || field)
                                                    .join(", ")}
                                            </td>
                                            <td className="py-3 pr-4">
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditDeadline(deadline)}
                                                        className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                                                    >
                                                        Bearbeiten
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteDeadline(deadline.id)}
                                                        disabled={deadlinesDeletingId === deadline.id}
                                                        className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                                    >
                                                        {deadlinesDeletingId === deadline.id ? "Löschen..." : "Löschen"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                </>
                )}
            </div>

            <div id="admin-gemaApi" className="hidden bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">GEMA</h3>
                        <p className="text-sm text-gray-500">Externe GEMA-Lizenzen und Veranstaltungen</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleSectionToggle("gemaApi")}
                        className="text-sm font-semibold text-red-600 hover:text-red-700"
                    >
                        {openSections.gemaApi ? "Schliessen" : "Öffnen"}
                    </button>
                </div>

                {openSections.gemaApi && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setGemaApiFilter(GEMA_API_FILTERS.ACTIVE)}
                                className={`rounded-lg px-3 py-2 text-xs font-semibold border ${
                                    gemaApiFilter === GEMA_API_FILTERS.ACTIVE
                                        ? "border-gray-900 bg-gray-900 text-white"
                                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                Alle aktiven
                            </button>
                            <button
                                type="button"
                                onClick={() => setGemaApiFilter(GEMA_API_FILTERS.MISSING)}
                                className={`rounded-lg px-3 py-2 text-xs font-semibold border ${
                                    gemaApiFilter === GEMA_API_FILTERS.MISSING
                                        ? "border-red-700 bg-red-700 text-white"
                                        : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                }`}
                            >
                                Setlist fehlt (Aktion erforderlich)
                            </button>
                            <button
                                type="button"
                                onClick={() => setGemaApiFilter(GEMA_API_FILTERS.DONE)}
                                className={`rounded-lg px-3 py-2 text-xs font-semibold border ${
                                    gemaApiFilter === GEMA_API_FILTERS.DONE
                                        ? "border-green-700 bg-green-700 text-white"
                                        : "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                                }`}
                            >
                                Erledigt
                            </button>
                            <label className="ml-2 inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={gemaApiShowCancelled}
                                    onChange={(event) => setGemaApiShowCancelled(event.target.checked)}
                                />
                                STORNIERT anzeigen
                            </label>
                            <button
                                type="button"
                                onClick={fetchGemaExternalData}
                                disabled={gemaApiLoading}
                                className="ml-auto rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                                {gemaApiLoading ? "Lade..." : "Neu laden"}
                            </button>
                            <button
                                type="button"
                                onClick={handleGemaApiRelogin}
                                disabled={gemaApiReloginLoading}
                                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                            >
                                {gemaApiReloginLoading ? "Re-Login..." : "Session erneuern"}
                            </button>
                        </div>

                        <div className="grid gap-3 md:grid-cols-3">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-xs text-gray-600">Lizenzen gesamt</p>
                                <p className="text-2xl font-bold text-gray-900">{gemaApiData.lizenzListe.length}</p>
                            </div>
                            <div className="rounded-lg border border-red-100 bg-red-50 p-3">
                                <p className="text-xs text-red-700">Setlist fehlt</p>
                                <p className="text-2xl font-bold text-red-700">{gemaApiMissingCount}</p>
                            </div>
                            <div className="rounded-lg border border-green-100 bg-green-50 p-3">
                                <p className="text-xs text-green-700">Setlist erledigt</p>
                                <p className="text-2xl font-bold text-green-700">{gemaApiDoneCount}</p>
                            </div>
                        </div>

                        {!!gemaApiData.errors?.veranstaltungen && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                Hinweis: {gemaApiData.errors.veranstaltungen}
                            </div>
                        )}

                        {Array.isArray(gemaApiData.veranstaltungsListe) && gemaApiData.veranstaltungsListe.length > 0 && (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-sm font-semibold text-gray-800">
                                    Externe Veranstaltungen: {gemaApiData.veranstaltungsListe.length}
                                </p>
                            </div>
                        )}

                        {gemaApiLoading ? (
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-red-600"></div>
                                GEMA-Daten werden geladen...
                            </div>
                        ) : gemaApiEntries.length === 0 ? (
                            <p className="text-sm text-gray-500">Keine passenden GEMA-Einträge gefunden.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="text-left text-xs uppercase text-gray-500 border-b">
                                        <tr>
                                            <th className="py-2 pr-4">Datum</th>
                                            <th className="py-2 pr-4">Event</th>
                                            <th className="py-2 pr-4">Ort</th>
                                            <th className="py-2 pr-4">Betrag</th>
                                            <th className="py-2 pr-4">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gemaApiEntries.map((entry, index) => {
                                            const musikfolgeStatus = String(entry?.musikfolgeStatus || "");
                                            const setlistMissing = musikfolgeStatus === "0";
                                            const setlistDone = musikfolgeStatus === "2" || musikfolgeStatus === "3";
                                            const isVoranmeldung = entry?.status === "NF_VORANMELDUNG";
                                            return (
                                                <tr
                                                    key={`external-gema-${entry?.lizenzId || entry?.eventBezeichnung || index}`}
                                                    className={`border-b last:border-none ${setlistMissing ? "bg-red-50/80" : ""}`}
                                                >
                                                    <td className="py-3 pr-4 font-medium text-gray-900">
                                                        {formatDate(entry?.eventDatumBeginn)}
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        <p className="font-medium text-gray-900">{entry?.eventBezeichnung || "-"}</p>
                                                        <p className="text-xs text-gray-500">Lizenz-ID: {entry?.lizenzId || "-"}</p>
                                                    </td>
                                                    <td className="py-3 pr-4 text-gray-700">{entry?.nutzungsOrt || "-"}</td>
                                                    <td className="py-3 pr-4 text-gray-700">{formatEuro(entry?.betrag)}</td>
                                                    <td className="py-3 pr-4">
                                                        <div className="flex flex-wrap gap-2">
                                                            {setlistMissing && (
                                                                <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white">
                                                                    Setlist fehlt! Aktion erforderlich
                                                                </span>
                                                            )}
                                                            {isVoranmeldung && (
                                                                <span className="inline-flex items-center rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white">
                                                                    Voranmeldung
                                                                </span>
                                                            )}
                                                            {setlistDone && (
                                                                <span className="inline-flex items-center rounded-full bg-green-600 px-3 py-1 text-xs font-semibold text-white">
                                                                    Setlist OK
                                                                </span>
                                                            )}
                                                            {!setlistMissing && !setlistDone && !isVoranmeldung && (
                                                                <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                                                                    {entry?.status || "Unbekannt"}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div id="admin-gema" className="hidden bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">GEMA-Verwaltung (intern)</h3>
                        <p className="text-sm text-gray-500">Übersicht für Tarife, Setlisten und Reminder</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleSectionToggle("gema")}
                        className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                        {openSections.gema ? "Schliessen" : "Öffnen"}
                    </button>
                </div>

                {openSections.gema && (
                    <div className="space-y-4">
                        <div className="grid gap-3 md:grid-cols-4">
                            <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3">
                                <p className="text-xs text-indigo-700">GEMA relevante Events</p>
                                <p className="text-2xl font-bold text-indigo-900">{gemaEvents.length}</p>
                            </div>
                            <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                                <p className="text-xs text-amber-700">Setlist Pflicht</p>
                                <p className="text-2xl font-bold text-amber-900">{gemaRequiredEvents.length}</p>
                            </div>
                            <div className="rounded-lg border border-rose-100 bg-rose-50 p-3">
                                <p className="text-xs text-rose-700">Setlist offen</p>
                                <p className="text-2xl font-bold text-rose-900">{gemaMissingSetlistCount}</p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-end">
                                <button
                                    type="button"
                                    onClick={handleDispatchGemaReminders}
                                    disabled={gemaDispatching}
                                    className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                                >
                                    {gemaDispatching ? "Reminder laufen..." : "Setlist-Reminder jetzt prüfen"}
                                </button>
                            </div>
                        </div>

                        {gemaEvents.length === 0 ? (
                            <p className="text-sm text-gray-500">Keine GEMA-relevanten Veranstaltungen gefunden.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="text-left text-xs uppercase text-gray-500 border-b">
                                        <tr>
                                            <th className="py-2 pr-4">Event</th>
                                            <th className="py-2 pr-4">Tarif</th>
                                            <th className="py-2 pr-4">Setlist</th>
                                            <th className="py-2 pr-4">Aktionen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gemaEvents.slice(0, 20).map((event) => (
                                            <tr key={`gema-${event.id}`} className="border-b last:border-none">
                                                <td className="py-3 pr-4">
                                                    <p className="font-medium text-gray-900">{event.title}</p>
                                                    <p className="text-xs text-gray-500">{formatDate(event.date)}</p>
                                                </td>
                                                <td className="py-3 pr-4">{event.gema_tarif_vorschlag || "-"}</td>
                                                <td className="py-3 pr-4">
                                                    {event.is_setlist_required
                                                        ? (event.setlist_status || "Ausstehend")
                                                        : "Nicht notwendig"}
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <div className="flex flex-wrap gap-2">
                                                        {event.is_setlist_required && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleGemaExport(event)}
                                                                disabled={gemaExportingId === event.id}
                                                                className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                                                            >
                                                                {gemaExportingId === event.id ? "Export..." : "Export XLSX"}
                                                            </button>
                                                        )}
                                                        {event.is_setlist_required && event.setlist_public_token && (
                                                            <a
                                                                href={resolveSetlistUrl(event.setlist_public_token)}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                                            >
                                                                Setlist-Link
                                                            </a>
                                                        )}
                                                        {!event.is_setlist_required && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleGemaExport(event, true)}
                                                                disabled={gemaExportingId === event.id}
                                                                className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                                            >
                                                                Export (Alle)
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div id="admin-events" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col gap-2">
                        <h3 className="text-xl font-bold text-gray-900">
                            Veranstaltungen ({sortedEvents.length})
                        </h3>
                        {openSections.events && (
                            <button
                                type="button"
                                data-testid="refresh-button"
                                onClick={handleRefreshEvents}
                                className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-2 self-start"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Aktualisieren
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => handleSectionToggle("events")}
                            className="text-sm font-semibold text-amber-700 hover:text-amber-800"
                        >
                            {openSections.events ? "Schliessen" : "Öffnen"}
                        </button>
                    </div>
                </div>

                {openSections.events && (
                <>
                <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Suche</span>
                            <input
                                type="text"
                                value={eventFilters.search}
                                onChange={(event) =>
                                    setEventFilters((prev) => ({ ...prev, search: event.target.value }))
                                }
                                placeholder="Titel, Name, E-Mail, ID"
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Status</span>
                            <select
                                value={eventFilters.status}
                                onChange={(event) =>
                                    setEventFilters((prev) => ({ ...prev, status: event.target.value }))
                                }
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                            >
                                {STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Kategorie</span>
                            <select
                                value={eventFilters.category}
                                onChange={(event) =>
                                    setEventFilters((prev) => ({ ...prev, category: event.target.value }))
                                }
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                            >
                                <option value="all">Alle Kategorien</option>
                                {categoryOptions.map((category) => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Monat</span>
                            <select
                                value={eventFilters.month}
                                onChange={(event) =>
                                    setEventFilters((prev) => ({ ...prev, month: event.target.value }))
                                }
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                            >
                                <option value="all">Alle Monate</option>
                                {MONTH_NAMES.map((label, index) => (
                                    <option key={label} value={index + 1}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Jahr</span>
                            <select
                                value={eventFilters.year}
                                onChange={(event) =>
                                    setEventFilters((prev) => ({ ...prev, year: event.target.value }))
                                }
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                            >
                                <option value="all">Alle Jahre</option>
                                {yearOptions.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-3 items-end">
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Sortierung</span>
                            <select
                                value={eventSort}
                                onChange={(event) => setEventSort(event.target.value)}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                            >
                                {SORT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 font-medium">Pro Seite</span>
                            <select
                                value={eventPageSize}
                                onChange={(event) => setEventPageSize(Number(event.target.value))}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                            >
                                {[10, 20, 30, 50].map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            onClick={() =>
                                setEventFilters({
                                    search: "",
                                    status: "pending",
                                    category: "all",
                                    month: "all",
                                    year: "all"
                                })
                            }
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                        >
                            Filter zurücksetzen
                        </button>
                    </div>
                </div>

                {sortedEvents.length === 0 ? (
                    <div className="text-center py-12" data-testid="no-pending-events">
                        <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-gray-600 text-lg">Keine Treffer für die aktuellen Filter</p>
                        <p className="text-gray-500 text-sm mt-2">Passe Filter oder Suche an, um Ergebnisse zu sehen.</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {pagedEvents.map((event) => {
                            const coverImage = resolveImageUrl(event.cover_image_url || event.image_url);
                            const galleryImages = Array.isArray(event.gallery_image_urls) ? event.gallery_image_urls : [];

                            return (
                                <div
                                    key={event.id}
                                    data-testid={`pending-event-${event.id}`}
                                    className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-200"
                                >
                                    <div className="flex flex-col lg:flex-row gap-6">
                                        {coverImage && (
                                            <div className="lg:w-64 flex-shrink-0">
                                                <img
                                                    src={coverImage}
                                                    alt={event.title}
                                                    className="w-full h-48 lg:h-full object-cover rounded-lg"
                                                />
                                            </div>
                                        )}

                                        <div className="flex-1 space-y-4">
                                            <div>
                                                <h4 className="text-xl font-bold text-gray-900 mb-2" data-testid="pending-event-title">
                                                    {event.title}
                                                </h4>
                                                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <span>{formatDate(event.date)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <span>{event.time}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                        </svg>
                                                        <span className={event.category_is_new ? "font-medium text-red-600" : "font-medium"}>
                                                            {decodeHtml(event.category)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                        </svg>
                                                        <span>{event.created_by_name}</span>
                                                    </div>
                                                </div>
                                                {hasWordpressIssue(event) && (
                                                    <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-red-900">
                                                        <p className="text-sm font-semibold">Nicht auf WordPress auffindbar</p>
                                                        <p className="mt-1 break-words text-xs">
                                                            {event.wordpress_publish_error || "Keine WordPress-Verknüpfung vorhanden. Bitte die Veranstaltung öffnen und erneut zu WordPress pushen."}
                                                        </p>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleWordpressRepublish(event.id)}
                                                            disabled={publishingWordpressId === event.id}
                                                            className="mt-3 rounded-lg bg-red-700 px-3 py-2 text-xs font-semibold text-white hover:bg-red-800 disabled:opacity-60"
                                                        >
                                                            {publishingWordpressId === event.id ? "Veröffentliche..." : "Erneut zu WordPress pushen"}
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="flex flex-wrap gap-3 items-end">
                                                    <label className="text-sm text-gray-700">
                                                        <span className="block mb-1 font-medium">Veranstalter</span>
                                                        <select
                                                            value={
                                                                assignmentSelections[event.id]
                                                                || event.created_by_email?.toLowerCase()
                                                                || ""
                                                            }
                                                            onChange={(eventValue) =>
                                                                handleAssignmentChange(event.id, eventValue.target.value)
                                                            }
                                                            className="min-w-[220px] rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                                        >
                                                            {organizerOptions.map((organizer) => {
                                                                const emailValue = organizer.email?.toLowerCase() || "";
                                                                const isOwner = emailValue
                                                                    && event.created_by_email?.toLowerCase() === emailValue;
                                                                const label = organizer.name || organizer.email || emailValue;
                                                                return (
                                                                    <option key={organizer.user_id} value={emailValue}>
                                                                        {isOwner ? `${label} (aktueller Besitzer)` : label}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAssignEvent(event)}
                                                        disabled={assignmentSavingId === event.id}
                                                        className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-100 disabled:opacity-60"
                                                    >
                                                        {assignmentSavingId === event.id ? "Zuweisen..." : "Zuweisen"}
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <div
                                                    className="max-h-64 overflow-y-auto break-words rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700 mb-3 leading-relaxed space-y-2 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_h4]:text-sm [&_h4]:font-semibold [&_h5]:text-sm [&_h5]:font-semibold [&_h6]:text-sm [&_h6]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-purple-600 [&_a]:underline"
                                                    dangerouslySetInnerHTML={{
                                                        __html: event.description
                                                            ? sanitizeHtml(event.description)
                                                            : "<p>Keine Beschreibung vorhanden.</p>"
                                                    }}
                                                />
                                                <div className="text-sm space-y-1">
                                                    <p>{[formatFlyerDate(event.date), formatFlyerTime(event.time)].filter(Boolean).join(" ")}</p>
                                                    <p><span className="italic text-gray-900">Eintritt:</span> {event.admission}</p>
                                                    {event.door_time && (
                                                        <p><span className="italic text-gray-900">Einlass:</span> {formatFlyerTime(event.door_time)}</p>
                                                    )}
                                                    {event.end_time && (
                                                        <p><span className="text-gray-900">Ende:</span> {formatFlyerTime(event.end_time)}</p>
                                                    )}
                                                    {event.gema_relevant && (
                                                        <>
                                                            <p><span className="font-semibold text-gray-900">GEMA Typ:</span> {GEMA_TYPE_LABELS[event.gema_event_type] || event.gema_event_type || "-"}</p>
                                                            <p><span className="font-semibold text-gray-900">GEMA Tarif:</span> {event.gema_tarif_vorschlag || "-"}</p>
                                                            <p><span className="font-semibold text-gray-900">Setlist Pflicht:</span> {event.is_setlist_required ? "Ja" : "Nein"}</p>
                                                            {event.is_setlist_required ? (
                                                                <p><span className="font-semibold text-gray-900">Setlist:</span> {event.setlist_status || "Ausstehend"}{event.setlist_deadline ? ` (Deadline ${formatDate(event.setlist_deadline)})` : ""}</p>
                                                            ) : (
                                                                <p>Für diesen Tarif ist keine Setlist notwendig.</p>
                                                            )}
                                                            {event.vinyl_only && (
                                                                <p><span className="font-semibold text-gray-900">Vinyl-Regel:</span> VR-Ö nicht anwendbar</p>
                                                            )}
                                                        </>
                                                    )}
                                                    <p>
                                                        <span className="font-semibold text-gray-900">Letzte Änderung:</span>{" "}
                                                        {formatDateTime(event.updated_at || event.created_at) || "Unbekannt"}
                                                    </p>
                                                    {(event.admin_note || event.additional_info) && (
                                                        <p><span className="font-semibold text-gray-900">Info an Admin:</span> {event.admin_note || event.additional_info}</p>
                                                    )}
                                                    {event.is_kultur_for_all && (
                                                        <p>
                                                            <span className="font-semibold text-gray-900">Kultur für alle:</span> Ja
                                                        </p>
                                                    )}
                                                    {(event.homepage_url || event.ticket_url) && (
                                                        <p className="flex flex-wrap gap-4">
                                                            {event.homepage_url && (
                                                                <a
                                                                    href={event.homepage_url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-purple-600 hover:text-purple-700 underline"
                                                                >
                                                                    Homepage
                                                                </a>
                                                            )}
                                                            {event.ticket_url && (
                                                                <a
                                                                    href={event.ticket_url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-purple-600 hover:text-purple-700 underline"
                                                                >
                                                                    Ticketlink
                                                                </a>
                                                            )}
                                                        </p>
                                                    )}
                                                    {(event.keytick_event_url || event.keytick_event_id) && (
                                                        <p className="flex flex-wrap gap-2 text-sm">
                                                            <span className="font-semibold text-gray-900">KeyTick:</span>
                                                            {event.keytick_event_url ? (
                                                                <a
                                                                    href={event.keytick_event_url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-purple-600 hover:text-purple-700 underline"
                                                                >
                                                                    Event öffnen
                                                                </a>
                                                            ) : (
                                                                <span className="text-gray-700">
                                                                    {event.keytick_event_id} (Link nicht konfiguriert)
                                                                </span>
                                                            )}
                                                        </p>
                                                    )}
                                                    {Array.isArray(event.pricing_tiers) && event.pricing_tiers.length > 0 && (
                                                        <div className="pt-2 space-y-1">
                                                            <p className="font-semibold text-gray-900">Staffelpreise:</p>
                                                            <ul className="list-disc list-inside text-gray-700">
                                                                {event.pricing_tiers.map((tier, index) => (
                                                                    <li key={index}>
                                                                        <span className="font-medium">{tier.label || "Preis"}:</span> {tier.price}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {galleryImages.length > 0 && (
                                                <div className="space-y-2">
                                                    <p className="text-sm font-semibold text-gray-900">Weitere Bilder</p>
                                                    <div className="flex flex-wrap gap-3">
                                                        {galleryImages.map((url, index) => (
                                                            <img
                                                                key={`${url}-${index}`}
                                                                src={resolveImageUrl(url)}
                                                                alt={`Galeriebild ${index + 1}`}
                                                                className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {event.status === "pending" && (
                                                <div className="space-y-2 pt-2">
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-sm font-semibold text-gray-900">Ablehnungsnachricht</p>
                                                        <span className="text-xs text-gray-500">optional</span>
                                                    </div>
                                                    <textarea
                                                        value={rejectionNotes[event.id] || ""}
                                                        onChange={(eventValue) =>
                                                            setRejectionNotes(prev => ({
                                                                ...prev,
                                                                [event.id]: eventValue.target.value
                                                            }))
                                                        }
                                                        rows={3}
                                                        placeholder="Kurze Nachricht an den Veranstalter"
                                                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500"
                                                    />
                                                </div>
                                            )}

                                            <div className="flex flex-col sm:flex-row gap-3 pt-4">
                                                {event.gema_relevant && event.is_setlist_required && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleGemaExport(event)}
                                                            disabled={gemaExportingId === event.id}
                                                            className="sm:w-auto w-full inline-flex items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                                                        >
                                                            {gemaExportingId === event.id ? "Export..." : "GEMA Setlist Export (XLSX)"}
                                                        </button>
                                                        {event.setlist_public_token && (
                                                            <a
                                                                href={resolveSetlistUrl(event.setlist_public_token)}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="sm:w-auto w-full inline-flex items-center justify-center rounded-lg border border-indigo-200 px-4 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                                                            >
                                                                Öffentlicher Setlist-Link
                                                            </a>
                                                        )}
                                                    </>
                                                )}
                                                <Link
                                                    to={`/create?eventId=${event.id}&returnTo=admin`}
                                                    aria-label="Veranstaltung bearbeiten"
                                                    title="Bearbeiten"
                                                    className="sm:w-auto w-full inline-flex items-center justify-center h-12 w-12 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 11l6.586-6.586a2 2 0 112.828 2.828L11.828 13.828a2 2 0 01-1.414.586H9v-3.414a2 2 0 01.586-1.414z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 19H5a2 2 0 01-2-2V8" />
                                                    </svg>
                                                </Link>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteEvent(event.id, event.title)}
                                                    disabled={deletingEventId === event.id}
                                                    className="sm:w-auto w-full inline-flex items-center justify-center h-12 w-12 rounded-full border border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors disabled:opacity-60"
                                                    title="Loeschen"
                                                    aria-label="Veranstaltung loeschen"
                                                >
                                                    {deletingEventId === event.id ? (
                                                        <span className="text-xs font-semibold">...</span>
                                                    ) : (
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-8 0h10l-1-2H8l-1 2z" />
                                                        </svg>
                                                    )}
                                                </button>
                                                {event.status === "pending" && (
                                                    <>
                                                        <button
                                                            data-testid={`approve-${event.id}`}
                                                            onClick={() => handleApprove(event.id)}
                                                            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            Genehmigen
                                                        </button>
                                                        <button
                                                            data-testid={`reject-${event.id}`}
                                                            onClick={() => handleReject(event.id)}
                                                            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                            Ablehnen
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm text-gray-600">
                                Zeige {sortedEvents.length === 0 ? 0 : pageStart + 1}
                                -{Math.min(pageEnd, sortedEvents.length)} von {sortedEvents.length}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setEventPage((prev) => Math.max(1, prev - 1))}
                                    disabled={eventPageSafe === 1}
                                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Zurück
                                </button>
                                <span className="text-sm text-gray-600">
                                    Seite {eventPageSafe} / {totalPages}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setEventPage((prev) => Math.min(totalPages, prev + 1))}
                                    disabled={eventPageSafe === totalPages}
                                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Weiter
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                </>
                )}
            </div>

            <div id="admin-users" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col gap-3 mb-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col gap-2">
                        <h3 className="text-xl font-bold text-gray-900">Benutzerrechte</h3>
                        <p className="text-sm text-gray-500">Teams-Benutzer verwalten und Admins zuweisen</p>
                        {openSections.users && (
                            <button
                                type="button"
                                onClick={handleRefreshUsers}
                                className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-2 self-start"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Aktualisieren
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => handleSectionToggle("users")}
                            className="text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                        >
                            {openSections.users ? "Schliessen" :"Öffnen"}
                        </button>
                    </div>
                </div>
                {renderUsersSection()}
            </div>
        </div>
    );
};

export default AdminPanel;
